import React from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_DELETE_HOLD_MS = 820;

type DeleteHoldButtonProps = {
  disabled?: boolean;
  onDelete: () => void;
  holdMs?: number;
  disabledTitle?: string;
  disabledAriaLabel?: string;
  title?: string;
  ariaLabel?: string;
  className?: string;
  holdBarClassName?: string;
  /** 짧은 클릭 확인 모달 제목 */
  confirmTitle?: string;
  confirmDescription?: string;
  confirmButtonLabel?: string;
  /** true면 짧은 클릭에서도 모달 없이 바로 삭제(기본: 확인 모달) */
  skipClickConfirm?: boolean;
  children?: React.ReactNode;
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  | 'disabled'
  | 'onPointerDown'
  | 'onPointerUp'
  | 'onPointerCancel'
  | 'onPointerLeave'
  | 'onContextMenu'
  | 'title'
  | 'aria-label'
  | 'type'
  | 'className'
  | 'children'
>;

/** 길게 눌러 바로 삭제 · 짧게 누르면 확인 모달 후 삭제 */
const DeleteHoldButton = React.memo(function DeleteHoldButton({
  disabled = false,
  onDelete,
  holdMs = DEFAULT_DELETE_HOLD_MS,
  disabledTitle = '삭제할 기록이 없습니다',
  disabledAriaLabel = '삭제할 기록이 없습니다',
  title = '짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제',
  ariaLabel = '삭제 (짧게: 확인, 길게: 바로 삭제)',
  className = 'cp-annual-behavior-delete-hold-btn',
  holdBarClassName = 'cp-annual-behavior-delete-hold-bar',
  confirmTitle = '삭제할까요?',
  confirmDescription = '삭제하면 되돌릴 수 없습니다.',
  confirmButtonLabel = '삭제',
  skipClickConfirm = false,
  children,
  ...buttonRest
}: DeleteHoldButtonProps) {
  const [deleteHoldProgress, setDeleteHoldProgress] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const deleteHoldStartRef = React.useRef<number | null>(null);
  const deleteHoldRafRef = React.useRef<number | null>(null);
  const deletePointerIdRef = React.useRef<number | null>(null);
  const deleteBtnRef = React.useRef<HTMLButtonElement>(null);
  const holdCommittedRef = React.useRef(false);
  const onDeleteRef = React.useRef(onDelete);
  onDeleteRef.current = onDelete;
  const confirmTitleId = React.useId();

  const cancelDeleteHold = React.useCallback(() => {
    if (deleteHoldRafRef.current != null) {
      cancelAnimationFrame(deleteHoldRafRef.current);
      deleteHoldRafRef.current = null;
    }
    deleteHoldStartRef.current = null;
    setDeleteHoldProgress(0);
  }, []);

  const commitDelete = React.useCallback(() => {
    if (holdCommittedRef.current) return;
    holdCommittedRef.current = true;
    if (deleteHoldRafRef.current != null) {
      cancelAnimationFrame(deleteHoldRafRef.current);
      deleteHoldRafRef.current = null;
    }
    deleteHoldStartRef.current = null;
    const btn = deleteBtnRef.current;
    const pid = deletePointerIdRef.current;
    deletePointerIdRef.current = null;
    setDeleteHoldProgress(0);
    if (btn && pid != null) {
      try {
        btn.releasePointerCapture(pid);
      } catch {
        /* noop */
      }
    }
    onDeleteRef.current();
    requestAnimationFrame(() => {
      holdCommittedRef.current = false;
    });
  }, []);

  const deleteHoldLoop = React.useCallback(() => {
    const start = deleteHoldStartRef.current;
    if (start == null) return;
    const p = Math.min(1, (performance.now() - start) / holdMs);
    setDeleteHoldProgress(p);
    if (p >= 1) {
      commitDelete();
      return;
    }
    deleteHoldRafRef.current = requestAnimationFrame(deleteHoldLoop);
  }, [commitDelete, holdMs]);

  const onDeletePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      holdCommittedRef.current = false;
      cancelDeleteHold();
      deleteHoldStartRef.current = performance.now();
      deletePointerIdRef.current = e.pointerId;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      deleteHoldRafRef.current = requestAnimationFrame(deleteHoldLoop);
    },
    [cancelDeleteHold, deleteHoldLoop, disabled]
  );

  const finishPointer = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, openConfirmIfShort: boolean) => {
      if (e.type === 'pointerleave' && e.currentTarget.hasPointerCapture(e.pointerId)) {
        return;
      }
      const start = deleteHoldStartRef.current;
      if (start == null) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* noop */
        }
      }
      deletePointerIdRef.current = null;
      const p = Math.min(1, (performance.now() - start) / holdMs);
      if (p >= 1) {
        commitDelete();
      } else {
        cancelDeleteHold();
        if (openConfirmIfShort && !skipClickConfirm && !disabled) {
          setConfirmOpen(true);
        }
      }
      e.stopPropagation();
    },
    [cancelDeleteHold, commitDelete, disabled, holdMs, skipClickConfirm]
  );

  React.useEffect(() => {
    return () => {
      if (deleteHoldRafRef.current != null) {
        cancelAnimationFrame(deleteHoldRafRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    holdCommittedRef.current = false;
    cancelDeleteHold();
    setConfirmOpen(false);
  }, [cancelDeleteHold, disabled]);

  React.useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        setConfirmOpen(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [confirmOpen]);

  const confirmPortal =
    confirmOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport cp-delete-hold-confirm-overlay"
            role="presentation"
            onClick={() => setConfirmOpen(false)}
            onMouseDown={(ev) => ev.stopPropagation()}
            onPointerDown={(ev) => ev.stopPropagation()}
          >
            <div
              className="cp-pin-confirm cp-pin-confirm--warn"
              role="dialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className="cp-pin-confirm-warn-icon" aria-hidden>
                !
              </div>
              <p id={confirmTitleId} className="cp-pin-confirm-warn-title">
                {confirmTitle}
              </p>
              {confirmDescription ? (
                <p className="cp-pin-confirm-warn-desc">{confirmDescription}</p>
              ) : null}
              <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                  onClick={() => setConfirmOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                  onClick={() => {
                    setConfirmOpen(false);
                    onDeleteRef.current();
                  }}
                >
                  {confirmButtonLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        {...buttonRest}
        ref={deleteBtnRef}
        type="button"
        className={`${className}${deleteHoldProgress > 0 ? ' is-delete-holding' : ''}`}
        onPointerDown={onDeletePointerDown}
        onPointerUp={(e) => finishPointer(e, true)}
        onPointerCancel={(e) => finishPointer(e, false)}
        onPointerLeave={(e) => finishPointer(e, false)}
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled}
        title={disabled ? disabledTitle : title}
        aria-label={disabled ? disabledAriaLabel : ariaLabel}
      >
        <span
          className={holdBarClassName}
          style={{ transform: `scaleX(${deleteHoldProgress})` }}
          aria-hidden
        />
        {children ?? (
          <img src={`${process.env.PUBLIC_URL || ''}/Trash.svg`} alt="" className="cp-todo-note-trash-icon" />
        )}
      </button>
      {confirmPortal}
    </>
  );
});

export default DeleteHoldButton;
